import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Uploader from "@/components/Uploader";
import { Tv, Check, DollarSign, Sparkles } from "lucide-react";

export default function PurchaseChannel() {
  const { user } = useAuth() || {};
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ slug: "", name: "", tagline: "", logo_url: "", color: "#D4AF37" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/channels/purchase/info").then(r => setInfo(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in first"); nav("/login"); return; }
    setLoading(true);
    try {
      const slugSafe = form.slug.replace(/[^a-z0-9-]/g, "-").toLowerCase();
      const { data } = await api.post("/channels/purchase", { ...form, slug: slugSafe, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setLoading(false); }
  };

  return (
    <div className="pt-16" data-testid="purchase-channel-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2 flex items-center gap-2"><Tv className="w-3 h-3"/>Own Your Broadcast</p>
        <h1 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">Launch Your Christian Channel</h1>
        <p className="text-white/70 max-w-2xl mb-10">Broadcast your ministry, sermons, or worship content directly on VOTLiving.tv. Self-service uploads, full schedule control, custom branding.</p>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-8">
          <div className="bg-[#0A0A0E] border border-[#D4AF37]/30 rounded-2xl p-8 shadow-[0_10px_50px_rgba(212,175,55,0.1)]">
            <div className="flex items-baseline gap-1 mb-4">
              <DollarSign className="w-6 h-6 text-[#D4AF37]"/>
              <span className="font-heading text-5xl font-bold text-[#D4AF37]">{info?.price_usd?.toFixed(0) || "…"}</span>
              <span className="text-white/50">/mo</span>
            </div>
            <p className="text-[11px] tracking-widest uppercase text-[#D4AF37] mb-4 flex items-center gap-1"><Sparkles className="w-3 h-3"/>Monthly Subscription</p>
            <p className="text-white/70 mb-6">Everything you need to run a professional Christian streaming channel.</p>
            <ul className="space-y-3">
              {(info?.includes || []).map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-white/85"><div className="w-6 h-6 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-[#D4AF37]"/></div>{it}</li>
              ))}
            </ul>
          </div>

          <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-8">
            <h3 className="font-heading text-2xl font-bold mb-1">Reserve Your Channel</h3>
            <p className="text-xs text-white/50 mb-6">Fill in the details below. You'll be redirected to Stripe to complete your subscription.</p>
            <form onSubmit={submit} className="space-y-3">
              <Field placeholder="Channel name (e.g., Grace Community TV)" value={form.name} onChange={v=>setForm({...form,name:v})} testid="pc-name" required/>
              <Field placeholder="Slug (e.g., grace-community, letters + hyphens)" value={form.slug} onChange={v=>setForm({...form,slug:v})} testid="pc-slug" required/>
              <Field placeholder="Tagline (short description shown next to your channel)" value={form.tagline} onChange={v=>setForm({...form,tagline:v})} testid="pc-tagline" required/>
              <div className="flex gap-2 items-center">
                <input placeholder="Logo URL" value={form.logo_url} onChange={e=>setForm({...form,logo_url:e.target.value})} className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="pc-logo" required />
                <Uploader accept="image/*" label="or upload" testid="pc-logo-upload" onUploaded={f => setForm(prev => ({...prev, logo_url: f.publicUrl}))} />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs text-white/60">Accent color</label>
                <input type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})} className="w-10 h-10 rounded border border-white/10 bg-transparent" data-testid="pc-color" />
              </div>
              <button disabled={loading} className="w-full btn-gold py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pc-submit">
                {loading ? "Redirecting…" : "Continue to payment →"}
              </button>
              <p className="text-[11px] text-white/40 text-center pt-2">Your channel appears in the TV Guide after payment. You'll be able to upload videos and set your schedule immediately.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ testid, ...props }) {
  return <input required={props.required} placeholder={props.placeholder} value={props.value} onChange={e=>props.onChange(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid={testid}/>;
}
